import React from 'react';
import { Navigate } from 'react-router-dom';
import { handleImport } from 'lib/import';
import ROUTES_PATH from 'domain/router/routesPathConfig';

import AppRoute from 'pages';
import RestrictedRoute from 'pages/RestrictedRoute';
import RestrictedRouteForPay from 'pages/RestrictedRouteForPay';
import RedirectRoute from 'pages/redirect';
import ExpiredRoute from 'pages/expired';
import AccessLimitedRoute from 'pages/accessLimited';
import Route404 from 'pages/servicePage/page404';
import DocumentNotFoundRoute from 'pages/servicePage/documentNotFound';
import BrowserNotSupportedRoute from 'pages/notSupported';
import IpadNotSupportedRoute from 'pages/notSupported/ipad';
import MobileBrowserNotSupportedRoute from 'pages/notSupported/mobile';
import DeferredTaskRoute from 'pages/deferredTask';

import IDPRoute from 'pages/idp';

import CompaniesRoute from 'pages/companies';

import CompanyWorkspaceStandardRoute from 'pages/company/WorkspaceRoute/standard';
import CompanyWorkspaceConfidentialRoute from 'pages/company/WorkspaceRoute/confidential';
import CompanyDashboardRoute from 'pages/company/DashboardRoute';
import CompanyDashboardVendorExpensesRoute from 'pages/company/DashboardRoute/pages/VendorExpensesRoute';
import CompanyBankFeedRoute from 'pages/company/BankFeedRoute';
import CompanyBankFeedBankTransactionsRoute from 'pages/company/BankFeedRoute/pages/BankTransactionsRoute';
import CompanyBankFeedCreditCardTransactionsRoute from 'pages/company/BankFeedRoute/pages/CreditCardTransactionsRoute';
import { BANK_FEED_ROUTE_TABS } from 'pages/company/BankFeedRoute/helper';
// DOKKA-PAY ROUTES
import PayCompanyRoute from 'pages/dokkaPay/pages/company';
import PayPaymentRoute from 'pages/dokkaPay/pages/payment';
import PayConfigurationsCompanyRoute from 'pages/dokkaPay/pages/configurations/company';
import PayCompanyProvidersRoute from 'pages/dokkaPay/pages/configurations/company/pages/paymentProviders';
import BeneficiariesRoute from 'pages/dokkaPay/pages/configurations/company/pages/beneficiaries';

import ErrorElement from 'lib/errorLogger/ErrorElement';

import LinearProgress from '@mui/material/LinearProgress';

const CompanyRoute = React.lazy(() => import('pages/company'));
const DocumentRoute = React.lazy(() => import('pages/document'));

// Configuration routes - lazy loaded
const OrganizationsRoute = React.lazy(() => import('pages/organizations'));
const ConfigurationsOrganizationRoute = React.lazy(() => import('pages/configurations/organization'));
const OrganizationGeneralRoute = React.lazy(() => import('pages/configurations/organization/pages/General'));
const OrganizationGeneralMainRoute = React.lazy(
  () => import('pages/configurations/organization/pages/General/Tabs/Main'),
);
const OrganizationGeneralContactRoute = React.lazy(
  () => import('pages/configurations/organization/pages/General/Tabs/Contact'),
);
const OrganizationGeneralFeaturesRoute = React.lazy(
  () => import('pages/configurations/organization/pages/General/Tabs/Features'),
);
const OrganizationBillingsRoute = React.lazy(() => import('pages/configurations/organization/pages/billings'));
const OrganizationConnectionsRoute = React.lazy(() => import('pages/configurations/organization/pages/users'));
const OrganizationSecurityRoute = React.lazy(() => import('pages/configurations/organization/pages/Security'));
const OrganizationBackupsRoute = React.lazy(() => import('pages/configurations/organization/pages/backups'));
const OrganizationKeysRoute = React.lazy(() => import('pages/configurations/organization/pages/keys'));

const ConfigurationsCompanyRoute = React.lazy(() => import('pages/configurations/company'));
const CompanyGeneralRoute = React.lazy(() => import('pages/configurations/company/pages/General'));
const CompanyGeneralMainRoute = React.lazy(() => import('pages/configurations/company/pages/General/Tabs/Main'));
const CompanyGeneralFeaturesRoute = React.lazy(
  () => import('pages/configurations/company/pages/General/Tabs/Features'),
);
const CompanyUsersRoute = React.lazy(() => import('pages/configurations/company/pages/users'));
const CompanyIntegrationsRoute = React.lazy(() => import('pages/configurations/company/pages/Integrations'));
const CompanyApplicationsRoute = React.lazy(() => import('pages/configurations/company/pages/applications'));
const CompanySupplierDefaultsRoute = React.lazy(() => import('pages/configurations/company/pages/vendorPreferences'));
const GeneralVendorApprovalManagement = React.lazy(
  () => import('pages/configurations/company/pages/vendorPreferences/pages/generalManagement'),
);
const PolicyVendorApprovalManagement = React.lazy(
  () => import('pages/configurations/company/pages/vendorPreferences/pages/policyManagement'),
);
const CompanyApprovalsRoute = React.lazy(() => import('pages/configurations/company/pages/approvals'));
const CompanyApprovalsFeaturesRoute = React.lazy(
  () => import('pages/configurations/company/pages/approvals/pages/ApprovalsFeatures'),
);
const CompanyApprovalsGroupsRoute = React.lazy(
  () => import('pages/configurations/company/pages/approvals/pages/groups'),
);
const CompanyApprovalsApproversRoute = React.lazy(
  () => import('pages/configurations/company/pages/approvals/pages/approvers'),
);
const CompanyApprovalsAdvancedGroupsRoute = React.lazy(
  () => import('pages/configurations/company/pages/approvals/pages/advancedGroups'),
);
const CompanyVendorBasedApprovalRoute = React.lazy(
  () => import('pages/configurations/company/pages/approvals/pages/vendorBasedApproval'),
);
const ConfigurationsCompanyBankFeedRoute = React.lazy(() => import('pages/configurations/company/pages/BankFeed'));
const CompanyBankFeedConnectionsRoute = React.lazy(
  () => import('pages/configurations/company/pages/BankFeed/pages/Connections'),
);
const CompanyBankFeedUsersRoute = React.lazy(() => import('pages/configurations/company/pages/BankFeed/pages/Users'));
const CompanyBankFeedSyncRoute = React.lazy(() => import('pages/configurations/company/pages/BankFeed/pages/Sync'));

const ProfileRoute = React.lazy(() => import('pages/profiles/company'));
const ProfileMainRoute = React.lazy(() => import('pages/profiles/company/pages/info'));
const ProfileNotificationsRoute = React.lazy(() => import('pages/profiles/company/pages/notifications'));
const ProfilePreferencesRoute = React.lazy(() => import('pages/profiles/company/pages/preferences'));

const MobileApprovalsRoute = React.lazy(() => import('pages/mobile/approvals'));

// Storybook — component state showcases, lazy loaded
const ChatStorybookPage = React.lazy(
  () => import('components/mui/Layouts/components/Chat/components/__showcase__/ChatStorybookPage'),
);
const ColorMismatchPage = React.lazy(
  () => import('components/mui/Layouts/components/Chat/components/__showcase__/ColorMismatchPage'),
);

import {
  waitBankFeedRouteLoadedSelector,
  waitCompanyIntegrationRouteLoadedSelector,
  waitCompanySettingsRouteLoadedSelector,
  waitDashboardRouteLoadedSelector,
} from 'domain/router/waitRoutesSelectors';

const routes = [
  {
    id: ROUTES_PATH.ROOT_LAYOUT.id,
    element: <AppRoute />,
    restriction: ROUTES_PATH.ROOT_LAYOUT.restriction,
    errorElement: <ErrorElement />,
    children: [
      {
        id: ROUTES_PATH.COMPANIES.id,
        path: ROUTES_PATH.COMPANIES.relative,
        index: true,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.COMPANIES.restriction}>
            <CompaniesRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/companies/sagas')),
      },
      {
        id: ROUTES_PATH.ACCESS_LIMITED.id,
        path: ROUTES_PATH.ACCESS_LIMITED.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.ACCESS_LIMITED.restriction}>
            <AccessLimitedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DOCUMENT_NOT_FOUND.id,
        path: ROUTES_PATH.DOCUMENT_NOT_FOUND.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DOCUMENT_NOT_FOUND.restriction}>
            <DocumentNotFoundRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_IPAD.id,
        path: ROUTES_PATH.NOT_SUPPORTED_IPAD.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_IPAD.restriction}>
            <IpadNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_BROWSER.id,
        path: ROUTES_PATH.NOT_SUPPORTED_BROWSER.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_BROWSER.restriction}>
            <BrowserNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.id,
        path: ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.restriction}>
            <MobileBrowserNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      // TODO: DEPRECATED (we do not support for now, maybe will be deleted in future)
      // {
      //   id: ROUTES_PATH.REQUEST_DOCUMENT.id,
      //   path: ROUTES_PATH.REQUEST_DOCUMENT.relative,
      //   element: (
      //     <RestrictedRoute restriction={ROUTES_PATH.REQUEST_DOCUMENT.restriction}>
      //       <RequestDocumentTransactionRoute />
      //     </RestrictedRoute>
      //   ),
      //   saga: () => handleImport(import('pages/requestTransaction/sagas')),
      // },
      {
        id: ROUTES_PATH.AUTH_IDP_LOGIN.id,
        path: ROUTES_PATH.AUTH_IDP_LOGIN.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.AUTH_IDP_LOGIN.restriction}>
            <IDPRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/idp/sagas')),
      },
      {
        id: ROUTES_PATH.AUTH_LOGIN.id,
        path: ROUTES_PATH.AUTH_LOGIN.relative,
        element: <Navigate to={ROUTES_PATH.COMPANIES.relative} replace />,
      },
      {
        id: ROUTES_PATH.REDIRECT.id,
        path: ROUTES_PATH.REDIRECT.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.REDIRECT.restriction}>
            <RedirectRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/redirect/sagas')),
      },
      {
        id: ROUTES_PATH.DEFERRED_TASK.id,
        path: ROUTES_PATH.DEFERRED_TASK.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DEFERRED_TASK.restriction}>
            <DeferredTaskRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.id,
        path: ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.restriction}>
            <MobileApprovalsRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/mobile/approvals/sagas')),
      },

      {
        id: ROUTES_PATH.ORGANIZATIONS.id,
        path: ROUTES_PATH.ORGANIZATIONS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATIONS.restriction}>
            <OrganizationsRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/organizations/sagas')),
      },
      {
        id: ROUTES_PATH.DOCUMENT.id,
        path: ROUTES_PATH.DOCUMENT.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DOCUMENT.restriction}>
            <DocumentRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/document/sagas')),
      },
      {
        id: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.id,
        path: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.restriction}>
            <LinearProgress sx={{ width: '100%' }} />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/company/WorkspaceRoute/redirectStandardSaga')),
      },
      {
        id: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.id,
        path: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.restriction}>
            <LinearProgress sx={{ width: '100%' }} />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/company/WorkspaceRoute/redirectConfidentialSaga')),
      },
      {
        id: ROUTES_PATH.COMPANY.id,
        path: ROUTES_PATH.COMPANY.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.COMPANY.restriction}>
            <CompanyRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/company/sagas')),
        children: [
          {
            id: ROUTES_PATH.COMPANY_WORKSPACE.id,
            path: ROUTES_PATH.COMPANY_WORKSPACE.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_WORKSPACE.restriction}>
                <CompanyWorkspaceStandardRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/company/WorkspaceRoute/sagas')),
          },

          {
            id: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.id,
            path: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.restriction}>
                <CompanyWorkspaceConfidentialRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/company/WorkspaceRoute/sagas')),
            sagasProps: {
              companyType: 'confidential',
            },
          },
          {
            id: ROUTES_PATH.COMPANY_DASHBOARD.id,
            path: ROUTES_PATH.COMPANY_DASHBOARD.relative,
            element: (
              <RestrictedRoute
                waitForSelector={waitDashboardRouteLoadedSelector}
                restriction={ROUTES_PATH.COMPANY_DASHBOARD.restriction}
              >
                <CompanyDashboardRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/company/DashboardRoute/sagas')), // TODO: dashboard saga
            children: [
              {
                id: ROUTES_PATH.COMPANY_DASHBOARD_VENDOR_EXPENSES.id,
                path: ROUTES_PATH.COMPANY_DASHBOARD_VENDOR_EXPENSES.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_DASHBOARD_VENDOR_EXPENSES.restriction}>
                    <CompanyDashboardVendorExpensesRoute />
                  </RestrictedRoute>
                ),
                saga: () => handleImport(import('pages/company/DashboardRoute/pages/VendorExpensesRoute/sagas')), // TODO: dashboard saga
              },
            ],
          },
          {
            id: ROUTES_PATH.COMPANY_BANK_FEED.id,
            path: ROUTES_PATH.COMPANY_BANK_FEED.relative,
            element: (
              <RestrictedRoute
                waitForSelector={waitBankFeedRouteLoadedSelector}
                restriction={ROUTES_PATH.COMPANY_BANK_FEED.restriction}
              >
                <CompanyBankFeedRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/company/BankFeedRoute/sagas')),
            children: [
              {
                index: true,
                element: <Navigate to={BANK_FEED_ROUTE_TABS.TRANSACTIONS} replace />,
              },
              {
                id: ROUTES_PATH.COMPANY_BANK_FEED_TRANSACTIONS.id,
                path: ROUTES_PATH.COMPANY_BANK_FEED_TRANSACTIONS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_BANK_FEED_TRANSACTIONS.restriction}>
                    <CompanyBankFeedBankTransactionsRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_BANK_FEED_CREDIT_CARDS.id,
                path: ROUTES_PATH.COMPANY_BANK_FEED_CREDIT_CARDS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_BANK_FEED_CREDIT_CARDS.restriction}>
                    <CompanyBankFeedCreditCardTransactionsRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
        ],
      },
      // ORGANIZATION SETTINGS
      {
        id: ROUTES_PATH.ORGANIZATION_SETTINGS.id,
        path: ROUTES_PATH.ORGANIZATION_SETTINGS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS.restriction}>
            <ConfigurationsOrganizationRoute />
          </RestrictedRoute>
        ),
        children: [
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.restriction}>
                <OrganizationBillingsRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.restriction}>
                <OrganizationConnectionsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/organization/pages/users/sagas')),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.restriction}>
                <OrganizationSecurityRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/organization/pages/Security/sagas')),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.restriction}>
                <OrganizationBackupsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/organization/pages/backups/sagas')),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.restriction}>
                <OrganizationGeneralRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/organization/pages/General/sagas')),
            children: [
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.restriction}>
                    <OrganizationGeneralMainRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.restriction}>
                    <OrganizationGeneralContactRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.restriction}>
                    <OrganizationGeneralFeaturesRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.restriction}>
                <OrganizationKeysRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/organization/pages/keys/sagas')),
          },
        ],
      },
      // COMPANY SETTINGS
      {
        id: ROUTES_PATH.COMPANY_SETTINGS.id,
        path: ROUTES_PATH.COMPANY_SETTINGS.relative,
        element: (
          <RestrictedRoute
            waitForSelector={waitCompanySettingsRouteLoadedSelector}
            restriction={ROUTES_PATH.COMPANY_SETTINGS.restriction}
          >
            <ConfigurationsCompanyRoute />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/configurations/company/sagas')),
        children: [
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_GENERAL.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_GENERAL.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_GENERAL.restriction}>
                <CompanyGeneralRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/General/sagas')),
            children: [
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_GENERAL_MAIN.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_GENERAL_MAIN.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_GENERAL_MAIN.restriction}>
                    <CompanyGeneralMainRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_GENERAL_FEATURES.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_GENERAL_FEATURES.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_GENERAL_FEATURES.restriction}>
                    <CompanyGeneralFeaturesRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_USERS.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_USERS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_USERS.restriction}>
                <CompanyUsersRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/users/sagas')),
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_INTEGRATIONS.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_INTEGRATIONS.relative,
            element: (
              <RestrictedRoute
                waitForSelector={waitCompanyIntegrationRouteLoadedSelector}
                restriction={ROUTES_PATH.COMPANY_SETTINGS_INTEGRATIONS.restriction}
              >
                <CompanyIntegrationsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/Integrations/sagas')),
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_APPLICATIONS.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_APPLICATIONS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPLICATIONS.restriction}>
                <CompanyApplicationsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/applications/sagas')),
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS.restriction}>
                <CompanySupplierDefaultsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/vendorPreferences/sagas')),
            children: [
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_GENERAL_MANAGEMENT.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_GENERAL_MANAGEMENT.relative,
                element: (
                  <RestrictedRoute
                    restriction={ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_GENERAL_MANAGEMENT.restriction}
                  >
                    <GeneralVendorApprovalManagement />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_POLICY_MANAGEMENT.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_POLICY_MANAGEMENT.relative,
                element: (
                  <RestrictedRoute
                    restriction={ROUTES_PATH.COMPANY_SETTINGS_SUPPLIER_DEFAULTS_POLICY_MANAGEMENT.restriction}
                  >
                    <PolicyVendorApprovalManagement />
                  </RestrictedRoute>
                ),
              },
            ],
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS.restriction}>
                <CompanyApprovalsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/approvals/sagas')),
            children: [
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_APPROVERS.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_APPROVERS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_APPROVERS.restriction}>
                    <CompanyApprovalsApproversRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_GROUPS.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_GROUPS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_GROUPS.restriction}>
                    <CompanyApprovalsGroupsRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_ADVANCED_GROUPS.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_ADVANCED_GROUPS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_ADVANCED_GROUPS.restriction}>
                    <CompanyApprovalsAdvancedGroupsRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_VENDOR_BASED_APPROVAL.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_VENDOR_BASED_APPROVAL.relative,
                element: (
                  <RestrictedRoute
                    restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_VENDOR_BASED_APPROVAL.restriction}
                  >
                    <CompanyVendorBasedApprovalRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_FEATURES.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_FEATURES.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_APPROVALS_FEATURES.restriction}>
                    <CompanyApprovalsFeaturesRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
          {
            id: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED.id,
            path: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED.restriction}>
                <ConfigurationsCompanyBankFeedRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/configurations/company/pages/BankFeed/sagas')),
            children: [
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_CONNECTIONS.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_CONNECTIONS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_CONNECTIONS.restriction}>
                    <CompanyBankFeedConnectionsRoute />
                  </RestrictedRoute>
                ),
                saga: () => handleImport(import('pages/configurations/company/pages/BankFeed/pages/Connections/sagas')),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_USERS.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_USERS.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_USERS.restriction}>
                    <CompanyBankFeedUsersRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_SYNC.id,
                path: ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_SYNC.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_SYNC.restriction}>
                    <CompanyBankFeedSyncRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
        ],
      },
      // ACCOUNT SETTINGS
      {
        id: ROUTES_PATH.PROFILE_SETTINGS.id,
        path: ROUTES_PATH.PROFILE_SETTINGS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS.restriction}>
            <ProfileRoute />
          </RestrictedRoute>
        ),
        saga: () => import('pages/profiles/company/sagas'),
        children: [
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_MAIN.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_MAIN.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_MAIN.restriction}>
                <ProfileMainRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/profiles/company/pages/info/sagas')),
          },
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.restriction}>
                <ProfileNotificationsRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/profiles/company/pages/notifications/sagas')),
          },
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.restriction}>
                <ProfilePreferencesRoute />
              </RestrictedRoute>
            ),
            saga: () => handleImport(import('pages/profiles/company/pages/preferences/sagas')),
          },
        ],
      },
      // STORYBOOK — component state showcases
      {
        id: ROUTES_PATH.STORYBOOK_CHAT.id,
        path: ROUTES_PATH.STORYBOOK_CHAT.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.STORYBOOK_CHAT.restriction}>
            <ChatStorybookPage />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.STORYBOOK_CHAT_COLORS.id,
        path: ROUTES_PATH.STORYBOOK_CHAT_COLORS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.STORYBOOK_CHAT_COLORS.restriction}>
            <ColorMismatchPage />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.EXPIRED.id,
        path: ROUTES_PATH.EXPIRED.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.EXPIRED.restriction}>
            <ExpiredRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.PAGE404.id,
        path: ROUTES_PATH.PAGE404.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.PAGE404.restriction}>
            <Route404 />
          </RestrictedRoute>
        ),
        saga: () => handleImport(import('pages/servicePage/sagas')),
      },
      // PAY MODULE
      {
        id: ROUTES_PATH.PAY_COMPANY.id,
        path: ROUTES_PATH.PAY_COMPANY.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY.restriction}>
            <PayCompanyRoute />
          </RestrictedRouteForPay>
        ),
        saga: () => handleImport(import('pages/dokkaPay/pages/company/sagas')),
      },
      {
        id: ROUTES_PATH.PAY_PAYMENT.id,
        path: ROUTES_PATH.PAY_PAYMENT.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_PAYMENT.restriction}>
            <PayPaymentRoute />
          </RestrictedRouteForPay>
        ),
        saga: () => handleImport(import('pages/dokkaPay/pages/payment/sagas')),
      },
      // PAY COMPANY SETTINGS
      {
        id: ROUTES_PATH.PAY_COMPANY_SETTINGS.id,
        path: ROUTES_PATH.PAY_COMPANY_SETTINGS.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS.restriction}>
            <PayConfigurationsCompanyRoute />
          </RestrictedRouteForPay>
        ),
        saga: () => handleImport(import('pages/dokkaPay/pages/configurations/company/sagas')),
        children: [
          {
            id: ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.id,
            path: ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.relative,
            element: (
              <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.restriction}>
                <PayCompanyProvidersRoute />
              </RestrictedRouteForPay>
            ),
            saga: () =>
              handleImport(import('pages/dokkaPay/pages/configurations/company/pages/paymentProviders/sagas')),
          },
          {
            id: ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.id,
            path: ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.relative,
            element: (
              <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.restriction}>
                <BeneficiariesRoute />
              </RestrictedRouteForPay>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;
